/**
 * Drives a layout change as a movement rather than a cut.
 *
 * Its own hook, and not a `useMemo` beside the layout, because it owns a clock. A layout is a pure
 * function of the graph and the settings; a transition is a thing that is happening, with a start
 * and an end - the same distinction that makes `useOrbitInteraction` a hook rather than a derived
 * value. Folding it into the memo that builds the layout would mean rebuilding the layout sixty
 * times a second in order to animate it.
 *
 * The start is captured *during the render that notices the new target*, not in an effect. Effects
 * run after render, and this render is the one that overwrites what was on screen - so capturing
 * there reads the arrangement that has just replaced the one the movement was supposed to start
 * from, and every mark travels from its destination to its destination. Which is a cut with extra
 * steps, and is exactly what the first version of this did.
 *
 * What it holds is the previous *drawn* arrangement rather than the previous layout. Those differ
 * when one change interrupts another: what a reader is looking at mid-movement is a half-finished
 * arrangement, and starting the next movement from the finished one it never reached would make
 * every mark jump before it travels.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { Link } from '../graph/topology';
import {
  toMatchRatio,
  toMovementRatio,
  toPositionsById,
  toTransitionPositions,
} from '../layout/transitionPositions';
import type { Vec3 } from '../types';
import { prefersReducedMotion } from '../../reducedMotion';

/**
 * How long a re-arrangement takes.
 *
 * Long enough to follow one mark across the picture, short enough not to be waited on. Under about
 * 300ms the eye loses the correspondence this exists to carry; much past 600ms and a reader who
 * changed a setting is watching an animation rather than reading a graph.
 */
const TRANSITION_MILLISECONDS = 450;

/**
 * How much of the new arrangement has to have been on screen for a movement to be worth it.
 *
 * Below this it is not a re-arrangement, it is a different picture - and animating into a different
 * picture sends every mark in from the middle at once, which reads as an explosion and claims a
 * continuity that is not there. Half is generous: a context change that keeps half its components
 * is recognisably the same estate, and one that keeps a tenth is not.
 */
const MINIMUM_MATCH_RATIO = 0.5;

/**
 * How far the marks have to travel for the movement to be worth making.
 *
 * A transition is not free even when nothing appears to move: references are drawn as straight
 * segments while it runs, so a re-arrangement that shifts everything by half a percent shows a
 * reader no movement at all and one clear artifact - the bundles coming apart and going back
 * together. That is strictly worse than cutting.
 *
 * Measured against a real case rather than chosen: removing one component from a grouped estate
 * moves the median mark 0.7% of the picture's width, because the packing puts almost everything
 * back where it was. Two percent sits above that and well below a regrouping.
 */
const MINIMUM_MOVEMENT_RATIO = 0.02;

export type LayoutTransitionArgs = {
  /**
   * `NetworkNode.matchKey` per drawn mark, parallel to `positions`.
   *
   * The match key rather than the node id, because a grouping renames every mark it touches - so
   * ids would match nothing across a rule change and the movement would be an explosion.
   */
  ids: readonly string[];
  /** Where the new layout puts them. */
  positions: readonly Vec3[];
  /** Index-space edges, for placing a node with no previous position. */
  links: readonly Link[];
  /**
   * Whether this layout is stable enough for a movement to mean anything.
   *
   * Off for the iterative layouts. Interpolating between two force solves from different seeds
   * sends every mark a long way to say nothing, which is worse than a cut - a cut at least does not
   * claim the marks are related.
   */
  isEnabled: boolean;
};

export type LayoutTransition = {
  /** Where to draw the marks now, which is the target arrangement unless one is in flight. */
  positions: readonly Vec3[];
  /**
   * Whether a movement is in flight.
   *
   * Stated rather than inferred. The caller has to know, because references are drawn differently
   * while the marks travel - and an earlier version left it to be worked out by comparing the
   * returned array against the one passed in, which is a signal that any accidental copy of an
   * array turns permanently on. A flag cannot get stuck that way.
   */
  isMoving: boolean;
};

export const useLayoutTransition = ({
  ids,
  positions,
  links,
  isEnabled,
}: LayoutTransitionArgs): LayoutTransition => {
  /**
   * When the movement ends, rather than how far through it is.
   *
   * A deadline cannot get stuck and an accumulator can. Progress held as state only advances while
   * something is advancing it, so a frame loop that dies - cancelled by a dependency change, a
   * remount, a change arriving faster than the movement finishes - leaves it below 1 forever, and
   * with it everything conditioned on "still moving". References drawn as straight lines during a
   * movement then stay straight for good, which is exactly what happened.
   *
   * Read off the clock instead, and the worst a lost frame loop can do is fail to repaint: the
   * next render for any reason computes the movement as over, because it is.
   */
  const endsAtRef = useRef(0);
  const [, setTick] = useState(0);

  const fromRef = useRef<ReadonlyMap<string, Vec3>>(new Map());
  /* What was last put on screen, which is where an interrupted movement resumes from. */
  const drawnRef = useRef<readonly Vec3[]>(positions);
  const drawnIdsRef = useRef<readonly string[]>(ids);
  const targetRef = useRef(positions);

  if (targetRef.current !== positions) {
    targetRef.current = positions;
    const from = toPositionsById(drawnIdsRef.current, drawnRef.current);
    const canMove =
      isEnabled &&
      from.size > 0 &&
      toMatchRatio(from, ids) >= MINIMUM_MATCH_RATIO &&
      toMovementRatio({ from, toIds: ids, toPositions: positions }) >=
        MINIMUM_MOVEMENT_RATIO &&
      !prefersReducedMotion();

    fromRef.current = canMove ? from : new Map();
    endsAtRef.current = canMove
      ? performance.now() + TRANSITION_MILLISECONDS
      : 0;
  }

  const now = performance.now();
  const remaining = endsAtRef.current - now;
  const isMoving = remaining > 0;
  const progress = isMoving ? 1 - remaining / TRANSITION_MILLISECONDS : 1;

  useEffect(() => {
    if (!isMoving) return;

    let frame: number | undefined;
    const step = () => {
      /* Re-render and let the clock decide. The loop carries no progress of its own, so a frame it
         misses is a frame not drawn rather than a movement that never ends. */
      setTick(tick => tick + 1);
      if (performance.now() < endsAtRef.current) {
        frame = requestAnimationFrame(step);
      }
    };
    frame = requestAnimationFrame(step);

    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
    /* On whether one is running, and nothing else. A movement that interrupts another does not
       need to restart the loop: the loop reads the deadline every frame, so a new deadline simply
       extends the one already turning. */
  }, [isMoving]);

  const transitioned = useMemo(
    () =>
      isMoving
        ? toTransitionPositions({
            from: fromRef.current,
            toIds: ids,
            toPositions: positions,
            links,
            progress,
          })
        : positions,
    [isMoving, progress, positions, ids, links]
  );

  /* Recorded after the fact, so the next movement starts from what this one last drew. Safe to
     write during render: the branch above reads it before replacing it. */
  drawnRef.current = transitioned;
  drawnIdsRef.current = ids;

  return { positions: transitioned, isMoving };
};
